import { Middleware } from "@reduxjs/toolkit";
import { RootState } from "../index";
import {
  addPowerSystem,
  removePowerSystem,
  addDriveSystem,
  removeDriveSystem,
  addFuelSystem,
  removeFuelSystem,
  addAccommodationSystem,
  removeAccommodationSystem,
  addBridgeSystem,
  removeBridgeSystem,
  addCargoSystem,
  removeCargoSystem,
  addDroneSystem,
  removeDroneSystem,
  addSensorSystem,
  removeSensorSystem,
  addExternalSystem,
  removeExternalSystem,
  addInternalSystem,
  removeInternalSystem,
  addSoftwareSystem,
  removeSoftwareSystem,
  addCommonAreaSystem,
  removeCommonAreaSystem,
  addStructureSystem,
  removeStructureSystem,
} from "../features/optionalSystemsSlice";
import { setPowerPlant, setFuelTanks } from "../features/shipBuilderSlice";
import { calculatePowerPlantRequirements } from "../../utils/calculations/powerCalculations";
import { calculateFuelTankRequirements } from "../../utils/calculations/fuelCalculations";

/**
 * Middleware to automatically update power plant and fuel tanks when optional systems are added or removed
 */
const optionalSystemsPowerPlantMiddleware: Middleware<{}, RootState> =
  (store) => (next) => (action: any) => {
    const result = next(action);

    // List of optional systems actions that affect power requirements
    const optionalSystemsActions = [
      addPowerSystem,
      removePowerSystem,
      addDriveSystem,
      removeDriveSystem,
      addFuelSystem,
      removeFuelSystem,
      addAccommodationSystem,
      removeAccommodationSystem,
      addBridgeSystem,
      removeBridgeSystem,
      addCargoSystem,
      removeCargoSystem,
      addDroneSystem,
      removeDroneSystem,
      addSensorSystem,
      removeSensorSystem,
      addExternalSystem,
      removeExternalSystem,
      addInternalSystem,
      removeInternalSystem,
      addSoftwareSystem,
      removeSoftwareSystem,
      addCommonAreaSystem,
      removeCommonAreaSystem,
      addStructureSystem,
      removeStructureSystem,
    ];

    // Check if this action is one of the optional systems actions
    const isOptionalSystemsAction = optionalSystemsActions.some(actionCreator =>
      actionCreator.match(action)
    );

    if (isOptionalSystemsAction) {
      const state = store.getState().shipBuilder;
      const optionalSystemsState = store.getState().optionalSystems;
      const { shipSize, techLevel, powerPlant } = state;

      // Calculate total power from all optional systems
      let optionalSystemsPowerRequirement = 0;

      // Power systems
      optionalSystemsState.power.forEach(system => {
        optionalSystemsPowerRequirement += system.power || 0;
      });

      // Drive systems
      optionalSystemsState.drives.forEach(system => {
        optionalSystemsPowerRequirement += system.power || 0;
      });

      // Fuel systems
      optionalSystemsState.fuel.forEach(system => {
        optionalSystemsPowerRequirement += system.power || 0;
      });

      // Accommodation systems
      optionalSystemsState.accommodations.forEach(system => {
        optionalSystemsPowerRequirement += system.power || 0;
      });

      // Bridge systems
      optionalSystemsState.bridge.forEach(system => {
        optionalSystemsPowerRequirement += system.power || 0;
      });

      // Cargo systems
      optionalSystemsState.cargo.forEach(system => {
        optionalSystemsPowerRequirement += system.power || 0;
      });

      // Drone systems
      optionalSystemsState.drones.forEach(system => {
        optionalSystemsPowerRequirement += system.power || 0;
      });

      // Sensor systems
      optionalSystemsState.sensors.forEach(system => {
        optionalSystemsPowerRequirement += system.power || 0;
      });

      // External systems
      optionalSystemsState.external.forEach(system => {
        optionalSystemsPowerRequirement += system.power || 0;
      });

      // Internal systems
      optionalSystemsState.internal.forEach(system => {
        optionalSystemsPowerRequirement += system.power || 0;
      });

      // Software systems (typically don't have power requirements, but checking anyway)
      optionalSystemsState.software.forEach(system => {
        optionalSystemsPowerRequirement += system.power || 0;
      });

      // Common area systems
      optionalSystemsState.commonAreas.forEach(system => {
        optionalSystemsPowerRequirement += system.power || 0;
      });

      // Structure systems
      optionalSystemsState.structure.forEach(system => {
        optionalSystemsPowerRequirement += system.power || 0;
      });

      // Modular hull systems (special case - check fitout power)
      optionalSystemsState.modularHull.forEach(system => {
        if (system.modules) {
          const fitoutPower = system.modules
            .filter(module => module.installed)
            .reduce((sum, module) => sum + (module.fitoutPower || 0), 0);
          optionalSystemsPowerRequirement += fitoutPower;
        }
      });

      // Check if current power plant can handle the new power requirements
      const currentPowerOutput = powerPlant ? powerPlant.power : 0;
      const basePowerRequired = state.shipStats?.powerRequired || (shipSize * 0.2);
      const totalPowerRequired = basePowerRequired + optionalSystemsPowerRequirement;

      if (currentPowerOutput < totalPowerRequired) {
        // Calculate new power plant requirements
        const powerPlantResult = calculatePowerPlantRequirements({
          powerRequired: totalPowerRequired,
          shipSize,
          techLevel,
        });

        if (powerPlantResult.success && powerPlantResult.data) {
          store.dispatch(setPowerPlant(powerPlantResult.data));
          
          // Update fuel tanks for new power plant
          const fuelTankResult = calculateFuelTankRequirements({
            powerPlant: powerPlantResult.data,
            drives: state.drives,
            shipSize,
          });
          
          if (fuelTankResult.success && fuelTankResult.data) {
            store.dispatch(setFuelTanks(fuelTankResult.data.total));
          }
        }
      }
    }

    return result;
  };

export default optionalSystemsPowerPlantMiddleware;
